import { castArray, uniqBy } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { Col, FormFeedback, Label, UncontrolledTooltip } from 'reactstrap'
import { useDebouncedCallback } from 'use-debounce'

export const AsyncSelectInput = ({
    inputField,
    isFieldArray,
    formik,
    fieldName,
    error,
    touched,
    inputComponents,
    inputValue,
    index,
    fieldArrayDeleteComponent,
    inputsLength,
    selectOptions,
    inputMargin,
}) => {
    const [defaultValue, setDefaultValue] = useState([])
    const [isFirstRun, setIsFirstRun] = useState(true)
    const [selectedValue, setSelectedValue] = useState([])
    const [debouncedTerm, setDebouncedTerm] = useState('')
    const { t } = useTranslation()

    const getValue = () => {
        if (selectOptions) {
            if (inputField.isMulti) {
                return selectOptions[!isFieldArray ? inputField.name : inputField.oldName]?.filter(
                    (option) => inputValue?.indexOf(option?.value?.toString()) >= 0
                )
            }

            return selectOptions[!isFieldArray ? inputField.name : inputField.oldName]?.filter((option) => {
                return option.value.toString() === inputValue?.toString()
            })
        }

        return []
    }

    useEffect(() => {
        if (inputValue && selectOptions && isFirstRun) {
            if (selectOptions) {
                const selectedOptions = getValue()

                if (selectedOptions?.length) {
                    setDefaultValue(selectedOptions)
                    setSelectedValue(selectedOptions)
                } else if (inputValue?.label) {
                    // storeFullOption case for fields: OWNER, DOCUMENT_CONTACT_ID of detail search
                    setDefaultValue([inputValue])
                    setSelectedValue(inputValue)
                }
            }
            setIsFirstRun(false)
        } else {
            if (!inputValue) {
                setSelectedValue('')
            } else {
                if (selectOptions) {
                    const selectedOptions = getValue()

                    if (selectedOptions) {
                        // selectedValue can be object or array
                        const selected = [...selectedOptions, ...castArray(selectedValue)]
                        const uniqueValues = uniqBy(selected, 'value')

                        setDefaultValue(selectedOptions)
                        setSelectedValue(uniqueValues)
                    }
                }
            }
        }
    }, [inputValue])

    const updateDebouncedTerm = useDebouncedCallback((value) => {
        setDebouncedTerm(value)
    }, 500)

    const params = useMemo(() => {
        if (!debouncedTerm) return {}

        const limitParams = inputField.noLimit ? { noLimit: 1 } : { limit: inputField.limit ?? 10 }
        
        const criteria = {
            ...(inputField.fixedCriteria ?? {}),
            ...inputField.searchCriteria?.reduce((acc, key) => ({ ...acc, [key]: debouncedTerm }), {}),
        }

        return { ...limitParams, criteria }
    }, [debouncedTerm, inputField.fixedCriteria, inputField.searchCriteria, inputField.noLimit, inputField.limit])

    const searchConfig = {
        params: params,
        options: {
            select: (data) => data.items?.map(inputField.formatOption) ?? [],
            enabled: !!debouncedTerm,
        },
    }

    const { data: searchResults, isFetching: isLoading } = inputField.useEntityHook(searchConfig.params, searchConfig.options)

    const displayOptions = useMemo(() => {
        return uniqBy([...defaultValue, ...(searchResults ?? [])], 'value')
    }, [defaultValue, searchResults])

    // propertyId, projectId, location
    const setFormikValues = (e) => {
        const values = e ? e.map((item) => item.value) : []
        const locations = e ? e.map((obj) => obj.location).join(' / ') : ''

        formik?.setFieldValue(fieldName, values)

        // since onChange adds all input items, first clear, then prefill location input with location data
        formik?.setFieldValue('location', '')
        formik?.setFieldValue('location', locations)
    }

    return (
        <>
            <Col
                key={inputField.name}
                {...inputField.colProps}
                className={`${inputField.colProps?.className ?? ''} ${!isFieldArray ? inputMargin : 'mb-2'}`}
            >
                {inputField.label && (
                    <div className="d-flex justify-content-between">
                        <Label for={fieldName} id={`${inputField.name}Lbl`}>
                            {inputField.label}
                            {inputField.required && <span className="required-mark">*</span>}
                        </Label>
                        {inputField.tooltip && (
                            <span id={`${inputField.name.toString().replace(/\./g, '')}Tooltip`}>
                                <i className="mdi mdi-help-circle-outline"></i>{' '}
                            </span>
                        )}
                    </div>
                )}

                <Select
                    onInputChange={(value) => updateDebouncedTerm(value)}
                    onChange={(e) => {
                        if (inputField.isMulti) {
                            setFormikValues(e)
                            setDefaultValue(e ? e : [])
                            setSelectedValue(e ? e.map((item) => item) : [])
                        } else {
                            formik?.setFieldValue(fieldName, inputField.storeFullOption ? e || '' : e?.value || '')
                            setDefaultValue(e ? [e] : [])
                            setSelectedValue(e || '')
                        }
                        if (inputField.handleChange) {
                            if (inputField.isMulti) {
                                inputField.handleChange(e ? e.map((item) => item.value) : [], formik, fieldName)
                            } else {
                                inputField.handleChange(e, formik, fieldName)
                            }
                        }
                    }}
                    onBlur={() => {
                        formik?.setFieldTouched(fieldName)
                    }}
                    options={displayOptions}
                    inputId={fieldName}
                    name={fieldName}
                    value={selectedValue ?? ''}
                    isDisabled={inputField.handleInputDisable ? inputField.handleInputDisable(formik, fieldName) : inputField.disabled}
                    isMulti={inputField.isMulti ?? false}
                    placeholder={inputField.placeholder ?? ''}
                    noOptionsMessage={() => t('form.common.select.noData')}
                    isLoading={isLoading}
                    loadingMessage={() => t('app.common.loading')}
                    className={error && touched ? 'is-invalid' : ''}
                    isClearable
                    {...inputField?.inputProps}
                />

                {error && touched ? <FormFeedback type="invalid">{error}</FormFeedback> : null}
                {inputComponents && inputComponents[inputField.name] && inputComponents[inputField.name]()}
                {inputField.tooltip && (
                    <UncontrolledTooltip placement="right" target={`${inputField.name.toString().replace(/\./g, '')}Tooltip`}>
                        {inputField.tooltip}
                    </UncontrolledTooltip>
                )}
            </Col>
            {isFieldArray && index === inputsLength && fieldArrayDeleteComponent}
        </>
    )
}

export default AsyncSelectInput
